import {Injectable, TemplateRef, Type} from '@angular/core';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {AppDrawerService} from './app-drawer.service';
import {AppDialogRef} from './app-dialog-ref';
import {appDialogDefaults} from './dialog.defaults';
import {AppTemplateDialogComponent} from './app-template-dialog.component';

export type AppDialogPresentation = 'modal' | 'drawer';

export interface AppDialogConfig {
	context?: unknown;
	closeOnBackdropClick?: boolean;
	closeOnEsc?: boolean;
	hasBackdrop?: boolean;
	dialogClass?: string;
	closable?: boolean;
	showHeader?: boolean;
	header?: string;
	width?: string;
	presentation?: AppDialogPresentation;
	/** Bouton X du shell drawer. Désactivé par défaut pour les composants (chrome interne). */
	shellCloseButton?: boolean;
	/** Header interne du contenu figé, corps scrollable (ex. détail résa). */
	stickyChrome?: boolean;
}

@Injectable({providedIn: 'root'})
export class AppDialogService {
	constructor(
		private readonly dialogService: DialogService,
		private readonly drawerService: AppDrawerService,
	) {
	}

	open<T>(content: Type<T> | TemplateRef<unknown>, config: AppDialogConfig = {}): AppDialogRef {
		if (config.presentation === 'drawer') {
			return this.drawerService.open(content, config);
		}
		return this.openModal(content, config);
	}

	private openModal<T>(content: Type<T> | TemplateRef<unknown>, config: AppDialogConfig): AppDialogRef {
		const appRef = new AppDialogRef();
		const closable = config.closable !== false;
		const showCloseButton = closable && (config.shellCloseButton ?? content instanceof TemplateRef);
		const dynamicConfig = appDialogDefaults({
			showHeader: config.showHeader ?? !(content instanceof TemplateRef),
			header: config.header,
			modal: config.hasBackdrop !== false,
			closable,
			dismissableMask: config.closeOnBackdropClick ?? closable,
			closeOnEscape: config.closeOnEsc ?? closable,
			styleClass: ['app-dynamic-dialog', config.dialogClass].filter(Boolean).join(' '),
			width: config.width,
		});

		let dynamicRef: DynamicDialogRef | null;

		if (content instanceof TemplateRef) {
			dynamicRef = this.dialogService.open(AppTemplateDialogComponent, {
				...dynamicConfig,
				showHeader: config.showHeader ?? false,
				data: {
					template: content,
					context: config.context,
					appRef,
					showCloseButton,
				},
			});
		} else {
			dynamicRef = this.dialogService.open(content, {
				...dynamicConfig,
				data: {
					...(config.context as object ?? {}),
					__appDialogRef: appRef,
				},
			});
			appRef.componentRef = dynamicRef as unknown;
		}

		if (!dynamicRef) {
			console.error('[AppDialog] PrimeNG a refusé d\'ouvrir la modale (duplicate?).');
			appRef.close();
			return appRef;
		}

		appRef.attachPrimeRef(dynamicRef);
		dynamicRef.onClose.subscribe((result) => appRef.notifyClosed(result));
		return appRef;
	}
}
